import { mode } from '@chakra-ui/theme-tools';

export const globalStyles = {
  colors: {
    // Primary Andruil brand colors - sophisticated deep blue-gray
    brand: {
      50: '#f7f8fa',
      100: '#edeef2',
      200: '#d6dae3',
      300: '#b2bbcc',
      400: '#8896b0',
      500: '#697899',
      600: '#566081',
      700: '#474e68',
      800: '#3d4357',
      900: '#36394a',
    },
    brandScheme: {
      50: '#f7f8fa',
      100: '#edeef2',
      200: '#d6dae3',
      300: '#b2bbcc',
      400: '#8896b0',
      500: '#697899',
      600: '#566081',
      700: '#474e68',
      800: '#3d4357',
      900: '#36394a',
    },
    brandTabs: {
      50: '#f7f8fa',
      100: '#edeef2',
      200: '#d6dae3',
      300: '#b2bbcc',
      400: '#8896b0',
      500: '#697899',
      600: '#566081',
      700: '#474e68',
      800: '#3d4357',
      900: '#36394a',
    },
    // Rich walnut - premium furniture accent
    anduril: {
      50: '#faf9f7',
      100: '#f0ede6',
      200: '#e2dccc',
      300: '#cfc5a8',
      400: '#bfad85',
      500: '#a89670',
      600: '#8a7a5e',
      700: '#6b5f4a',
      800: '#5a4f3e',
      900: '#4d4336',
    },
    // Sophisticated grays for modern office furniture
    modernGray: {
      50: '#f8f9fa',
      100: '#f1f3f4',
      200: '#e8eaed',
      300: '#dadce0',
      400: '#bdc1c6',
      500: '#9aa0a6',
      600: '#80868b',
      700: '#5f6368',
      800: '#3c4043',
      900: '#202124',
    },
    // Executive gold - luxury accent
    executive: {
      50: '#fef7ed',
      100: '#fdebd4',
      200: '#f9d4a8',
      300: '#f4b571',
      400: '#ed8d38',
      500: '#e77118',
      600: '#d8580e',
      700: '#b4420e',
      800: '#903513',
      900: '#762e13',
    },
    // Contract green - approval and success states
    contract: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
    // Warning red for urgent items
    urgent: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    // Professional blue for information
    professional: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    // Neutral backgrounds
    background: {
      50: '#fafafa',
      100: '#ffffff',
      200: '#f9fafb',
      300: '#f3f4f6',
      400: '#e5e7eb',
      500: '#d1d5db',
      600: '#9ca3af',
      700: '#6b7280',
      800: '#374151',
      900: '#111827',
    },
    // Secondary grays for subtle elements
    secondaryGray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    // Status colors
    red: {
      100: '#fee2e2',
      500: '#ef4444',
      600: '#dc2626',
    },
    blue: {
      50: '#eff6ff',
      500: '#3b82f6',
    },
    orange: {
      100: '#fed7aa',
      500: '#f97316',
    },
    green: {
      100: '#dcfce7',
      500: '#22c55e',
    },
    // Navy for professional headers
    navy: {
      50: '#f0f4f8',
      100: '#d9e2ec',
      200: '#bcccdc',
      300: '#9fb3c8',
      400: '#829ab1',
      500: '#627d98',
      600: '#486581',
      700: '#334e68',
      800: '#243b53',
      900: '#102a43',
    },
    gray: {
      100: '#f7fafc',
    },
  },
  fonts: {
    heading: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    body: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  radii: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
  },
  styles: {
    global: (props: any) => ({
      body: {
        overflowX: 'hidden',
        bg: mode('background.100', 'background.900')(props),
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        letterSpacing: '-0.02em',
        fontSize: 'md',
        lineHeight: '1.6',
        color: mode('gray.800', 'gray.100')(props),
      },
      input: {
        color: mode('gray.700', 'gray.200')(props),
      },
      html: {
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      },
      '::-webkit-scrollbar': {
        width: '6px',
      },
      '::-webkit-scrollbar-track': {
        bg: mode('gray.100', 'gray.700')(props),
      },
      '::-webkit-scrollbar-thumb': {
        bg: mode('gray.300', 'gray.500')(props),
        borderRadius: '3px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        bg: mode('gray.400', 'gray.400')(props),
      },
    }),
  },
}; 